import React, { useEffect, useRef } from 'react';
import { usePathname, useGlobalSearchParams } from 'expo-router';
import { useAppDispatch } from '../redux/hooks';
import { setLastVisitedRoute } from '../redux/navigationSlice';
import { trackScreen, trackScreenLeave } from '../services/analyticsService';

interface NavigationTrackerProps {
  children: React.ReactNode;
}

export const NavigationTracker: React.FC<NavigationTrackerProps> = ({ children }) => {
  const pathname = usePathname();
  // Global, not local: this component lives in the ROOT layout, and
  // useLocalSearchParams is scoped to its own route segment — up there it is
  // always empty. That is why /week-recap?week=... was persisted as a bare
  // /week-recap for as long as this has existed.
  const params = useGlobalSearchParams();
  const dispatch = useAppDispatch();
  const lastTrackedPath = useRef<string | null>(null);

  useEffect(() => {
    // Skip if same path (avoid duplicate tracking)
    if (pathname === lastTrackedPath.current) return;

    // Fire pageleave for the screen we're navigating away from
    if (lastTrackedPath.current) {
      trackScreenLeave(lastTrackedPath.current);
    }

    lastTrackedPath.current = pathname;

    trackScreen(pathname || '/');
  }, [pathname]);

  // Persistence is deliberately a separate effect from the analytics one above.
  // Params settle a render after the pathname does, i.e. with the pathname
  // unchanged, which the `pathname === lastTrackedPath.current` guard above
  // discards. Keeping the two apart means params still get recorded, while
  // pageviews stay one-per-screen rather than one per param change.
  useEffect(() => {
    // Only persist navigation state for authenticated app routes — not auth/onboarding screens
    if (pathname.startsWith('/auth') || pathname.startsWith('/onboarding')) return;

    const paramsRecord: Record<string, string> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (typeof value === 'string') {
        paramsRecord[key] = value;
      } else if (Array.isArray(value) && value.length > 0) {
        paramsRecord[key] = value[0];
      }
    });

    dispatch(setLastVisitedRoute({
      path: pathname,
      params: Object.keys(paramsRecord).length > 0 ? paramsRecord : undefined,
    }));
  }, [pathname, JSON.stringify(params), dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  return <>{children}</>;
};
